import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Collapse,
  Stack,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Cancel,
  HelpOutline,
} from '@mui/icons-material';

function getScoreColor(score) {
  if (score >= 750) return '#4caf50'; // Green
  if (score >= 650) return '#8bc34a'; // Light green
  if (score >= 580) return '#ffc107'; // Yellow
  if (score >= 500) return '#ff9800'; // Orange
  return '#f44336'; // Red
}

function getScoreLabel(score) {
  if (score >= 750) return 'Excelente';
  if (score >= 650) return 'Muy Bueno';
  if (score >= 580) return 'Bueno';
  if (score >= 500) return 'Regular';
  return 'Bajo';
}

export default function ClienteScore({ scoring, loading = false }) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Paper sx={{ p: 2, backgroundColor: 'white' }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          Calculando score...
        </Typography>
      </Paper>
    );
  }

  if (!scoring) {
    return (
      <Paper sx={{ p: 2, backgroundColor: 'white' }}>
        <Typography variant="body2" color="text.secondary">
          Score no disponible
        </Typography>
      </Paper>
    );
  }

  const score = scoring.score || 0;
  const scoreColor = getScoreColor(score);
  const scoreLabel = getScoreLabel(score);

  const metricLabels = {
    pagos_puntuales: 'Pagos Puntuales',
    prestamos_completados: 'Préstamos Completados',
    mora: 'Mora',
    antiguedad: 'Antigüedad',
    volumen: 'Volumen',
  };

  const formatMetricValue = (metricKey, value) => {
    if (metricKey === 'pagos_puntuales' || metricKey === 'prestamos_completados') {
      return `${value.toFixed(1)}%`;
    }
    if (metricKey === 'mora') {
      return `${value} días`;
    }
    if (metricKey === 'antiguedad') {
      return `${value.toFixed(1)} meses`;
    }
    if (metricKey === 'volumen') {
      return `${value} préstamos`;
    }
    return value;
  };

  return (
    <Paper sx={{ p: 2, backgroundColor: 'white' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={800}>
          Score de Crédito
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          aria-label="expand"
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: scoreColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            {Math.round(score)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Chip
            label={scoreLabel}
            sx={{
              backgroundColor: scoreColor,
              color: 'white',
              fontWeight: 600,
              mb: 0.5,
            }}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            Rango: 300-850
          </Typography>
        </Box>
      </Stack>

      {/* Cedula validation status */}
      {scoring.validacion_cedula && (
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {scoring.validacion_cedula.valida === true ? (
              <>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Cédula válida</Typography>
              </>
            ) : scoring.validacion_cedula.valida === false ? (
              <>
                <Cancel color="error" fontSize="small" />
                <Typography variant="body2">Cédula inválida</Typography>
              </>
            ) : (
              <>
                <HelpOutline color="action" fontSize="small" />
                <Typography variant="body2">Cédula no validada</Typography>
              </>
            )}
          </Stack>
        </Box>
      )}

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Desglose Detallado
          </Typography>

          {/* Overall weights */}
          <Box sx={{ mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Componentes Principales:
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                Historial: {scoring.peso_historial?.toFixed(0)}%
              </Typography>
              <Typography variant="body2">
                Cédula: {scoring.peso_cedula?.toFixed(0)}%
              </Typography>
            </Stack>
          </Box>

          {/* Metrics breakdown */}
          {scoring.desglose_historial && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Métrica</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Puntos</TableCell>
                    <TableCell align="right">Peso</TableCell>
                    <TableCell align="right">Ponderado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(scoring.desglose_historial).map(([key, metric]) => (
                    <TableRow key={key}>
                      <TableCell>
                        <Tooltip title={metricLabels[key] || key}>
                          <Typography variant="body2">
                            {metricLabels[key] || key}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatMetricValue(key, metric.valor)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{metric.puntos}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{metric.peso}%</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {metric.puntos_ponderados.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" fontWeight={600}>
                        Score de Historial
                      </Typography>
                    </TableCell>
                    <TableCell align="right" colSpan={2}>
                      <Typography variant="body2" fontWeight={700}>
                        {scoring.score_historial?.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Additional info */}
          {scoring.metricas && (
            <Box sx={{ mt: 2, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Información Adicional:
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Typography variant="body2">
                  Total de préstamos: {scoring.metricas.total_prestamos}
                </Typography>
                <Typography variant="body2">
                  Préstamos completados: {scoring.metricas.prestamos_completados}
                </Typography>
                {scoring.metricas.pagos_totales > 0 && (
                  <Typography variant="body2">
                    Pagos puntuales: {scoring.metricas.pagos_puntuales} / {scoring.metricas.pagos_totales}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

